import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Inline } from '../../design-system/inline/inline';
import { Input } from '../../design-system/input/input';
import { Stack } from '../../design-system/stack/stack';
import { TabsContent } from '../../design-system/tabs/tabs-primitives';
import Header from '../layout/header';
import { AddAllAccountsCallout } from './components/add-all-accounts-callout/add-all-accounts-callout';
import { AskAiDrawer } from './components/ask-ai/ask-ai-drawer';
import { ReportingDataTableWrapper } from './components/data-table-components/reporting-data-table-wrapper';
import CashFlowWrapper from './tabs/cash-flow/cash-flow-wrapper';
import { ReportingTabsWrapper } from './tabs/tabs-wrapper';
import { reportingCalloutStorage } from './utils/reporting-callout-storage';

const REPORT_TAB_VALUE = 'REPORT';

const ReportingPage = () => {
  const { reportingPage, isAskAiEnabled } = useFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleComplete = () => {
    reportingCalloutStorage.setDismissed(true);
  };

  const [askAiQuery, setAskAiQuery] = useState('');
  const [isAskAiOpen, setIsAskAiOpen] = useState(false);
  const handleAskSubmit = () => {
    setIsAskAiOpen(true);
  };

  return (
    <Stack align="stretch" className="min-h-screen p-2.5">
      <Stack align="stretch" className="flex-1 overflow-clip rounded-lg bg-white">
        <div className="overflow-clip">
          <Header />
        </div>
        {reportingPage ? (
          <Stack align="stretch" className="bg-white" space={10}>
            <Stack space={4} className="px-32">
              <Inline justify="between" className="h-[42px] w-full">
                <h1 className="text-2xl font-semibold text-black">Reporting</h1>

                {isAskAiEnabled && (
                  <Input
                    placeholder="✨ Ask AI about your finances..."
                    value={askAiQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAskAiQuery(e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && askAiQuery.trim()) {
                        e.preventDefault();
                        handleAskSubmit();
                      }
                    }}
                    clearable
                    onClear={() => setAskAiQuery('')}
                    className="w-[520px]"
                  />
                )}
              </Inline>
              <AddAllAccountsCallout
                onLinkAllAccounts={() => navigate('/dashboard/accounts/connect-open-banking')}
                onComplete={handleComplete}
              />
            </Stack>
            <ReportingTabsWrapper
              triggers={[{ value: 'CASH_FLOW' }, { value: 'REPORT' }]}
              onValueChange={(value) => {
                if (value !== REPORT_TAB_VALUE) {
                  searchParams.delete('id');
                  searchParams.delete('view');

                  setSearchParams(searchParams);
                }
              }}
              children={
                <>
                  <TabsContent value="CASH_FLOW">
                    <CashFlowWrapper />
                  </TabsContent>
                  <TabsContent value="REPORT" className="px-32">
                    <ReportingDataTableWrapper />
                  </TabsContent>
                </>
              }
            />
          </Stack>
        ) : (
          <ReportingDataTableWrapper />
        )}

        <AskAiDrawer isOpen={isAskAiOpen} initialQuery={askAiQuery} onClose={() => setIsAskAiOpen(false)} />
      </Stack>
    </Stack>
  );
};

export default ReportingPage;
